// src/context/Web3Context.js
// GR33D-SIMPLIFIED-V2: [2025-02-28] - Web3Context avec gestion améliorée des connexions

import { ethers } from 'ethers';
import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { PROXY_ADDRESS, CONTRACT_ABI, NETWORK_CONFIG } from '../constants/contract';
import Logger from '../utils/logger';

// Create context
export const Web3Context = createContext(null);

export const Web3Provider = ({ children }) => {
    // États
    const [account, setAccount] = useState(null);
    const [provider, setProvider] = useState(null);
    const [contract, setContract] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const initialCheckDone = useRef(false);
    const signerRef = useRef(null); // Référence pour garder trace du signer
    
    // Notifications
    const [notifications, setNotifications] = useState([]);
    
    // Ajouter une notification
    const addNotification = useCallback((message, type = 'info', autoClose = 5000, txHash = null) => {
        const id = Date.now();
        const notification = { id, message, type, txHash };
        
        console.log(`[Notification - ${type}]: ${message}`);
        setNotifications(prev => [...prev, notification]);
        
        if (autoClose) {
            setTimeout(() => {
                setNotifications(prev => prev.filter(notif => notif.id !== id));
            }, autoClose);
        }
        
        return id;
    }, []);
    
    // Fonction pour créer et initialiser le contrat
    const initializeContract = useCallback((ethProvider, walletAddress) => {
        try {
            if (!ethProvider || !walletAddress) {
                console.log('Cannot initialize contract: missing provider or address');
                return null;
            }
            
            // Récupération du signer
            const signer = ethProvider.getSigner();
            signerRef.current = signer;
            
            console.log('Initializing contract with:', {
                address: PROXY_ADDRESS,
                abiMethods: CONTRACT_ABI.filter(item => item.type === 'function').length,
                signer: signer._address || walletAddress
            });
            
            // Création du contrat
            const contractInstance = new ethers.Contract(
                PROXY_ADDRESS,
                CONTRACT_ABI,
                signer
            );
            
            // Vérification de base que le contrat est correctement initialisé
            if (!contractInstance || !contractInstance.address) {
                console.error('Contract initialization failed: contract unavailable');
                return null;
            }
            
            console.log('Contract initialized successfully:', {
                address: contractInstance.address,
                hasBalance: typeof contractInstance.balanceOf === 'function',
                hasStake: typeof contractInstance.stake === 'function',
                hasLPStaking: typeof contractInstance.stakeLPTokens === 'function',
                hasVesting: typeof contractInstance.vestingCount === 'function'
            });
            
            return contractInstance;
        } catch (error) {
            console.error('Contract initialization error:', error);
            return null;
        }
    }, []);
    
    // Vérifier l'état de connexion au chargement - AMÉLIORÉ AVEC PERSISTANCE
    useEffect(() => {
        const checkConnection = async () => {
            if (initialCheckDone.current) return;
            
            try {
                console.log('Checking existing connection...');
                initialCheckDone.current = true;
                
                // Vérifier si localStorage a une connexion sauvegardée
                const savedAccount = localStorage.getItem('connectedAccount');
                
                // Si une connexion précédente existe et que MetaMask est disponible
                if (savedAccount && window.ethereum) {
                    console.log('Found saved connection for:', savedAccount);
                    setIsConnecting(true);
                    
                    // Vérifier si cette connexion est toujours valide
                    try {
                        const accounts = await window.ethereum.request({ 
                            method: 'eth_accounts' 
                        });
                        
                        if (accounts && accounts.length > 0) {
                            const currentAccount = accounts[0];
                            console.log('Account connected:', currentAccount);
                            
                            // Connexion toujours valide, restaurer l'état
                            const ethProvider = new ethers.providers.Web3Provider(window.ethereum);
                            
                            // Mise à jour du provider
                            setProvider(ethProvider);
                            
                            // Initialisation du contrat
                            const contractInstance = initializeContract(ethProvider, currentAccount);
                            if (contractInstance) {
                                // Mise à jour de l'état
                                setContract(contractInstance);
                                setAccount(currentAccount);
                                
                                // Configuration des listeners
                                setupEventListeners();
                                
                                console.log('Connection restored successfully');
                            } else {
                                throw new Error('Failed to initialize contract');
                            }
                        } else {
                            // Compte déconnecté, effacer localStorage
                            console.log('No accounts found');
                            localStorage.removeItem('connectedAccount');
                        }
                    } catch (connectionError) {
                        console.error('Connection restoration error:', connectionError);
                        localStorage.removeItem('connectedAccount');
                        addNotification('Failed to restore connection', 'error');
                    }
                } else {
                    console.log('No saved connection found or MetaMask not available');
                }
            } catch (error) {
                console.error('Error checking connection:', error);
                localStorage.removeItem('connectedAccount');
            } finally {
                setIsConnecting(false);
            }
        };
        
        checkConnection();
    }, [addNotification, initializeContract]);
    
    // Configuration des listeners d'événements
    const setupEventListeners = useCallback(() => {
        if (window.ethereum) {
            const handleAccountsChanged = (accounts) => {
                console.log('Accounts changed:', accounts);
                if (!accounts || accounts.length === 0) {
                    // Déconnecté
                    handleDisconnect();
                    addNotification('Wallet disconnected', 'info');
                    localStorage.removeItem('connectedAccount');
                } else {
                    // Changement de compte - recréer le contrat avec le nouveau compte
                    const newAccount = accounts[0];
                    
                    setAccount(newAccount);
                    // Sauvegarder en localStorage
                    localStorage.setItem('connectedAccount', newAccount);
                    
                    // Réinitialiser le contrat avec le nouveau compte si provider existe
                    if (provider) {
                        const contractInstance = initializeContract(provider, newAccount);
                        if (contractInstance) {
                            setContract(contractInstance);
                        }
                    }
                    
                    addNotification(`Connected to ${newAccount.substring(0, 6)}...`, 'success');
                }
            };
            
            const handleChainChanged = () => {
                console.log('Network changed, reloading...');
                window.location.reload();
            };
            
            const handleDisconnect = () => {
                console.log('Provider disconnected');
                setAccount(null);
                setProvider(null);
                setContract(null);
                signerRef.current = null;
                localStorage.removeItem('connectedAccount');
            };
            
            // Nettoyer les listeners précédents si nécessaire
            if (window.ethereum.removeListener) {
                window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
                window.ethereum.removeListener('chainChanged', handleChainChanged);
                window.ethereum.removeListener('disconnect', handleDisconnect);
            }
            
            // Ajouter les nouveaux listeners
            window.ethereum.on('accountsChanged', handleAccountsChanged);
            window.ethereum.on('chainChanged', handleChainChanged);
            window.ethereum.on('disconnect', handleDisconnect);
            
            // Renvoyer une fonction de nettoyage
            return () => {
                if (window.ethereum.removeListener) {
                    window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
                    window.ethereum.removeListener('chainChanged', handleChainChanged);
                    window.ethereum.removeListener('disconnect', handleDisconnect);
                }
            };
        }
    }, [addNotification, initializeContract, provider]);
    
    // Nettoyage à la déconnexion
    const handleDisconnect = useCallback(() => {
        setAccount(null);
        setProvider(null);
        setContract(null);
        signerRef.current = null;
        localStorage.removeItem('connectedAccount');
    }, []);
    
    // Fonction de connexion
    const connectWallet = useCallback(async () => {
        // Éviter les connexions multiples
        if (isConnecting) return false;
        
        try {
            // Début de connexion
            setIsConnecting(true);
            setError(null);
            
            // Vérifier MetaMask
            if (!window.ethereum) {
                throw new Error('MetaMask not installed');
            }
            
            // Demander les comptes
            const accounts = await window.ethereum.request({ 
                method: 'eth_requestAccounts' 
            });
            
            if (!accounts || accounts.length === 0) {
                throw new Error('No accounts found');
            }
            
            // Créer provider et contrat
            const ethProvider = new ethers.providers.Web3Provider(window.ethereum);
            const address = accounts[0];
            
            // Mise à jour du provider
            setProvider(ethProvider);
            
            // Initialisation du contrat
            const contractInstance = initializeContract(ethProvider, address);
            if (!contractInstance) {
                throw new Error('Failed to initialize contract');
            }
            
            // Mise à jour de l'état
            setContract(contractInstance);
            setAccount(address);
            
            // Sauvegarder en localStorage pour persistance
            localStorage.setItem('connectedAccount', address);
            
            // Configuration des listeners
            setupEventListeners();
            
            // Notification de succès
            addNotification('Wallet connected successfully!', 'success');
            
            console.log('Connected to:', address);
            return true;
            
        } catch (error) {
            console.error('Connection error:', error);
            
            // Formatage de l'erreur
            const errorMessage = error.code === 4001 
                ? 'User rejected connection request'
                : error.message || 'Failed to connect';
                
            setError(errorMessage);
            addNotification(errorMessage, 'error');
            
            // Reset
            handleDisconnect();
            
            return false;
            
        } finally {
            // Toujours réinitialiser isConnecting
            setIsConnecting(false);
        }
    }, [isConnecting, setupEventListeners, addNotification, handleDisconnect, initializeContract]);
    
    // Fonction de déconnexion
    const disconnectWallet = useCallback(async () => {
        try {
            // Nettoyage
            handleDisconnect();
            
            // Supprimer les listeners
            if (window.ethereum && window.ethereum.removeAllListeners) {
                window.ethereum.removeAllListeners();
            }
            
            // Supprimer données localStorage
            localStorage.removeItem('connectedAccount');
            
            addNotification('Wallet disconnected successfully', 'success');
            console.log('Disconnected');
            
            return true;
            
        } catch (error) {
            console.error('Disconnect error:', error);
            addNotification('Error disconnecting wallet', 'error');
            return false;
        }
    }, [handleDisconnect, addNotification]);
    
    // Formatage d'adresse
    const formatAddress = useCallback((address) => {
        if (!address) return '';
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    }, []);
    
    // Valeur du contexte
    const contextValue = {
        account,
        provider,
        contract,
        isConnecting,
        error,
        connectWallet,
        disconnectWallet,
        formatAddress,
        notifications,
        addNotification,
        removeNotification: (id) => {
            setNotifications(prev => prev.filter(notif => notif.id !== id));
        },
        // Exposer le signer si nécessaire
        getSigner: () => signerRef.current
    };
    
    return (
        <Web3Context.Provider value={contextValue}>
            {children}
        </Web3Context.Provider>
    );
};

// Hook pour utiliser le contexte
export const useWeb3 = () => {
    const context = React.useContext(Web3Context);
    
    if (!context) {
        throw new Error('useWeb3 must be used within a Web3Provider');
    }
    
    return context;
};

export default Web3Provider;
